import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, type Auth } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { initAppCheck } from './appCheck';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const appCheckReady: Promise<void> = initAppCheck(app).catch((err) => {
  console.error('[appCheck] init failed — callables will lack App Check tokens:', err);
});

// On Capacitor native, construct Auth with initializeAuth + an explicit
// persistence instead of getAuth(). getAuth() auto-probes the available storage
// backends on construction to recover an existing session, and that probe hangs
// on the capacitor://localhost WebView scheme — wedging Auth init entirely
// (onAuthStateChanged never fires its initial callback; setPersistence and
// signInWithCredential never resolve). initializeAuth sets persistence at
// construction and skips the probe. indexedDBLocalPersistence both unwedges init
// and survives cold starts, so the real signed-in identity is restored on
// relaunch and Firestore/callables act as the real uid (not anonymous).
export const auth: Auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);

// Firestore with built-in offline persistence (IndexedDB cache + write queue).
// Same cache works for v1 (subscription state, encrypted OAuth tokens) and
// v2 when domain data (tasks/routines/habits) migrates to Firestore.
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
